
import type { InputOnChangeData } from "@fluentui/react-components";
import { Button, Input, Link, makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses, Spinner, tokens } from "@fluentui/react-components";
import type { IGraphPickerItem } from "../../../interfaces";
import { DismissRegular, SearchRegular } from "@fluentui/react-icons";
import type { CSSProperties } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { searchUsersAsync, searchGroupsAsync } from "../../../apiRequests/graph/graph";
import { isNullOrEmptyOrWhiteSpace, normalizeGuid, uniqueId } from "../../../utilities/StringUtilities/StringUtilities";
import { UserPickerSearchItem } from "./UserPickerSearchItem";
import { UserPickerSelectedItem } from "./UserPickerSelectedItem";
import { restoreFocusOnBlurEventAsync } from "../../../utilities/BrowserUtilities/BrowserUtilities";
import { useMsal } from "@azure/msal-react";

export interface IUserPickerProps {
    selectedPeople: IGraphPickerItem[];
    onPeopleSelect(people: IGraphPickerItem[]): void;
    includeGroups: boolean;
    isEnabled: boolean;
}

const buildStyles = makeStyles({
    outer: {
        position: "relative",
        border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
        borderRadius: tokens.borderRadiusMedium,
        display: "flex",
        minHeight: "32px",
        boxSizing: "border-box",
        flexDirection: "column",
        "&:has(:focus-visible)": {
            border: `1px solid ${tokens.colorCompoundBrandStroke}`,
            "& :focus-within::after": {
                display: "none !important"
            }
        }
    },
    selectedOuter: {
        position: "relative",
        display: "flex",
        width: "calc(100% - 32px)",
        flexShrink: "1",
        flexGrow: "1",
        paddingTop: "2px",
        paddingLeft: "2px",
        marginRight: "32px"
    },
    inputOuter: {
        position: "relative",
        display: "flex",
        width: "100%",
        minHeight: "0px",
        maxHeight: "0px",
        overflow: "hidden",
        alignItems: "center"
    },
    inputShow: {
        minHeight: "32px",
        maxHeight: "32px"
    },
    input: {
        position: "relative",
        flexGrow: "1",
        flexShrink: "1",
        height: "100%",
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: "transparent",
        border: "0px solid transparent",
        "& .fui-Input__input": {
            padding: "0 6px"
        }
    },
    button: {
        position: "relative",
        padding: "0",
        minWidth: "0",
        height: "32px",
        aspectRatio: "1/1",
        border: "1px solid transparent",
        borderRadius: tokens.borderRadiusMedium,
        flexGrow: "0",
        flexShrink: "0"
    },
    focusSearchButton: {
        position: "absolute",
        top: "0",
        right: "1px",
        padding: "0",
        minWidth: "0",
        height: "32px",
        aspectRatio: "1/1",
        borderRadius: `0 ${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium} 0`
    },
    menu: {
        width: "100%",
        maxWidth: "100%"
    },
    focusClickArea: {
        position: "absolute",
        top: "0",
        left: "0",
        height: "100%",
        width: "100%"
    },
    dismissArea: {
        position: "fixed",
        top: "0",
        left: "0",
        height: "100VH",
        width: "100VW",
        cursor: "default"
    },
    peoplePicker: {
        paddingTop: "20px",
        paddingBottom: "20px",
        maxWidth: "400px"
    }
});

export const UserPicker = (props: IUserPickerProps) => {
    const { isEnabled, includeGroups, selectedPeople, onPeopleSelect } = props;
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [searchResults, setSearchResults] = useState<IGraphPickerItem[]>([]);

    const outerRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const searchResultItemClass = useMemo(() => { return uniqueId(); }, []);

    const outerRefRect = outerRef.current?.getBoundingClientRect();
    const popoverStyle: CSSProperties | undefined = outerRefRect
        ? { maxHeight: `calc(100VH - ${Math.floor(outerRefRect.bottom)}px - 10px)`, width: `${Math.floor(outerRefRect.width)}px` }
        : undefined;

    const styleClasses = buildStyles();

    const showDismissArea = isOpen || document.activeElement === inputRef.current;
    const showInput = isOpen || document.activeElement === inputRef.current || selectedPeople.length <= 0;

    const openPopupAsync = async () => {
        if (isOpen) {
            inputRef.current?.focus();
            return;
        }

        setIsOpen(true);

        if (inputRef.current) {
            inputRef.current.focus();
            await restoreFocusOnBlurEventAsync(inputRef.current);
        }
    };

    const searchAsync = useCallback(async (searchFor: string) => {
        try {
            setLoading(true);
            setSearchResults([]);

            const results: IGraphPickerItem[] = includeGroups
                ? (await Promise.all([
                    searchUsersAsync(searchFor, instance, account),
                    searchGroupsAsync(searchFor, instance, account)
                ])).flat()
                : await searchUsersAsync(searchFor, instance, account);

            setSearchResults(results);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [instance, account, includeGroups]);

    const inputOnChange = async (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): Promise<void> => {
        ev.stopPropagation();

        const value = data.value.replace(/^\s+/, "");
        setSearchValue(value);

        if (isNullOrEmptyOrWhiteSpace(value)) {
            setSearchResults([]);
            return;
        }

        await openPopupAsync();
        await searchAsync(value);
    };

    const clearButtonOnClick = async (ev: React.MouseEvent): Promise<void> => {
        ev.stopPropagation();

        if (isOpen && selectedPeople.length <= 0) {
            setIsOpen(false);
            setLoading(false);
            setSearchValue("");
            setSearchResults([]);
            return;
        }

        const searchVal = isOpen ? "" : searchValue;
        setSearchValue(searchVal);
        setSearchResults([]);
        setLoading(true);

        await openPopupAsync();
        await searchAsync(searchVal);
    };

    const userResultOnSelect = (userInfo: IGraphPickerItem): void => {
        const normalizedId = normalizeGuid(userInfo.id);
        const newSelected = [...selectedPeople];
        const existingIndex = newSelected.findIndex((s) => normalizeGuid(s.id) === normalizedId);

        if (existingIndex >= 0) {
            newSelected.splice(existingIndex, 1);
        } else {
            newSelected.push(userInfo);
        }

        setIsOpen(false);
        setLoading(false);

        onPeopleSelect(newSelected);
    };

    const inputOnFocus = async (): Promise<void> => {
        if (isNullOrEmptyOrWhiteSpace(searchValue)) {
            return;
        }

        if (isOpen) {
            return;
        }

        await openPopupAsync();

        if (searchResults.length > 0) {
            return;
        }

        await searchAsync(searchValue);
    };

    const dismissAreaOnClick = (ev: React.MouseEvent): void => {
        ev.preventDefault();
        ev.stopPropagation();

        setIsOpen(false);
    };

    const focusClickAreaOnClick = async (ev: React.MouseEvent): Promise<void> => {
        ev.preventDefault();
        ev.stopPropagation();

        await openPopupAsync();
    };

    const focusClickAreaOnKeyUp = async (ev: React.KeyboardEvent): Promise<void> => {
        if (ev.key === "Enter") {
            await openPopupAsync();
        }
    };

    const inputOnKeyUp = (ev: React.KeyboardEvent): void => {

        if (ev.key !== "ArrowDown" || searchResults.length <= 0) {
            return;
        }

        const searchItems = menuRef.current?.getElementsByClassName(searchResultItemClass);

        if (!searchItems || searchItems.length <= 0) {
            return;
        }

        const firstItem = searchItems[0];
        if (firstItem instanceof HTMLElement) {
            firstItem.focus();
            firstItem.dataset.fuiFocusVisible = "true";
        }
    };

    return (
        <div className={styleClasses.peoplePicker}>
            {
                showDismissArea &&
                <Link className={styleClasses.dismissArea} onClick={dismissAreaOnClick} aria-label="dismiss-area" />
            }
            <Menu open={isOpen}>
                {/* disableButtonEnhancement: without it MenuTrigger treats this div as an ARIA button and
                    preventDefaults Space on keydown, so spaces never reach the search Input */}
                <MenuTrigger disableButtonEnhancement>
                    <div className={styleClasses.outer} ref={outerRef}>
                        {
                            selectedPeople.length > 0 &&
                            <div className={styleClasses.selectedOuter}>
                                <Link className={styleClasses.focusClickArea} onClick={focusClickAreaOnClick} />
                                {
                                    selectedPeople.map((u) => (<UserPickerSelectedItem key={`selected_${u.id}`} userInfo={u} onSelect={userResultOnSelect} />))
                                }
                            </div>
                        }
                        {
                            !showInput &&
                            <Button appearance="subtle"
                                className={styleClasses.focusSearchButton}
                                disabled={!isEnabled}
                                onClick={focusClickAreaOnClick}
                                onKeyUp={focusClickAreaOnKeyUp}>
                                <SearchRegular />
                            </Button>
                        }
                        <div className={mergeClasses(styleClasses.inputOuter, showInput ? styleClasses.inputShow : "")}>
                            <Input value={searchValue}
                                type="text"
                                placeholder={includeGroups ? "Search people and groups" : "Search people"}
                                className={styleClasses.input}
                                disabled={!isEnabled}
                                onChange={inputOnChange}
                                onKeyUp={inputOnKeyUp}
                                onFocus={inputOnFocus}
                                ref={inputRef} />
                            <Button appearance="subtle" className={styleClasses.button} disabled={!isEnabled} onClick={clearButtonOnClick}>
                                {
                                    isOpen ? <DismissRegular /> : <SearchRegular />
                                }
                            </Button>
                        </div>
                    </div>
                </MenuTrigger>
                <MenuPopover className={styleClasses.menu} style={popoverStyle} ref={menuRef}>
                    <MenuList>
                        {
                            loading &&
                            <MenuItem icon={<Spinner size="tiny" />}>Searching...</MenuItem>
                        }
                        {
                            !loading && searchResults.length <= 0 &&
                            <MenuItem>{includeGroups ? "No users or groups found" : "No users found"}</MenuItem>
                        }
                        {
                            !loading && searchResults.length > 0 &&
                            searchResults.map((r) => (<UserPickerSearchItem key={`searchItem_${r.id}`} className={searchResultItemClass} userInfo={r} selectedUsers={selectedPeople} multiSelect={true} onSelect={userResultOnSelect} />))
                        }
                    </MenuList>
                </MenuPopover>
            </Menu>
        </div>
    );
};